/**
 * i18n entry point — locale registry, current-locale state, the `t()` helper,
 * and boot-time locale detection (URL override + `navigator.language` +
 * fallback to English).
 *
 * Design notes:
 *  - All strings live in **flat-keyed catalogues** (one file per language
 *    under `./<code>.ts`, the English catalogue is the canonical key set).
 *  - Determinism is unaffected: the simulation never reads any of this
 *    layer; only the UI / rendering / notice code calls `t()`.
 *  - Unknown / malformed locale ids silently fall back to English. No
 *    surface-area change for the default-English path.
 *  - Sprint 58 (i18n-10langs-s58) — added DE / PL / SK / HU / FR / ES / IT
 *    / ZH / JA / KO. Browser-language matching is **prefix-based**: a
 *    BCP-47 code like `zh-CN`, `pt-BR` or `de-AT` is reduced to its first
 *    sub-tag (`zh`, `pt`, `de`) and matched against the registry.
 */
import { cs } from './cs';
import { de } from './de';
import { en, type Catalogue } from './en';
import { es } from './es';
import { fr } from './fr';
import { hu } from './hu';
import { it } from './it';
import { ja } from './ja';
import { ko } from './ko';
import { pl } from './pl';
import { sk } from './sk';
import { zh } from './zh';

/** Identifier for a registered locale — opaque string, sanitised on input. */
export type LocaleId =
  | 'en'
  | 'cs'
  | 'de'
  | 'pl'
  | 'sk'
  | 'hu'
  | 'fr'
  | 'es'
  | 'it'
  | 'zh'
  | 'ja'
  | 'ko';

/**
 * Every locale shipped with the game. Order is significant — UI listing
 * (the title-screen LANG chip cycle) walks this array.
 */
export const LOCALES: readonly LocaleId[] = [
  'en',
  'cs',
  'de',
  'pl',
  'sk',
  'hu',
  'fr',
  'es',
  'it',
  'zh',
  'ja',
  'ko',
];

/** Locale id → catalogue. Adding a new locale: add a file, register it here. */
const CATALOGUES: Record<LocaleId, Catalogue> = {
  en,
  cs,
  de,
  pl,
  sk,
  hu,
  fr,
  es,
  it,
  zh,
  ja,
  ko,
};

/**
 * Public per-locale meta (display name + short code), keyed by id. Used by
 * the title-screen locale switcher chip and the locale-aware UI in general.
 */
export const LOCALE_META: Record<LocaleId, { name: string; code: string }> = {
  en: { name: en.meta.name, code: en.meta.code },
  cs: { name: cs.meta.name, code: cs.meta.code },
  de: { name: de.meta.name, code: de.meta.code },
  pl: { name: pl.meta.name, code: pl.meta.code },
  sk: { name: sk.meta.name, code: sk.meta.code },
  hu: { name: hu.meta.name, code: hu.meta.code },
  fr: { name: fr.meta.name, code: fr.meta.code },
  es: { name: es.meta.name, code: es.meta.code },
  it: { name: it.meta.name, code: it.meta.code },
  zh: { name: zh.meta.name, code: zh.meta.code },
  ja: { name: ja.meta.name, code: ja.meta.code },
  ko: { name: ko.meta.name, code: ko.meta.code },
};

/** True when `id` names a shipped locale. */
export function isLocaleId(id: string): id is LocaleId {
  return (LOCALES as readonly string[]).includes(id);
}

/** The active locale id — `'en'` until set otherwise. */
let activeLocale: LocaleId = 'en';

/** Listeners notified whenever the active locale changes. */
const listeners = new Set<() => void>();

/**
 * Switch the active locale. An unknown id is silently dropped (the call is a
 * no-op) so a malformed URL param never crashes the boot. Fires every
 * registered listener synchronously when the locale actually changes.
 */
export function setLocale(id: string): void {
  if (!isLocaleId(id)) return;
  if (id === activeLocale) return;
  activeLocale = id;
  for (const fn of listeners) fn();
}

/** The active locale id (canonical, post-sanitisation). */
export function getLocale(): LocaleId {
  return activeLocale;
}

/** Subscribe to locale changes. Returns a disposer that un-registers. */
export function onLocaleChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// ----------------------------------------------------------------------------
// Key resolution + interpolation
// ----------------------------------------------------------------------------

/** Walk a dotted-key path against a catalogue, returning `undefined` on a miss. */
function lookup(catalogue: unknown, key: string): string | undefined {
  const parts = key.split('.');
  let cursor: unknown = catalogue;
  for (const part of parts) {
    if (cursor === null || cursor === undefined) return undefined;
    if (typeof cursor !== 'object') return undefined;
    cursor = (cursor as Record<string, unknown>)[part];
  }
  return typeof cursor === 'string' ? cursor : undefined;
}

/** Substitute `{var}` placeholders in `template` against `vars`. */
function interpolate(template: string, vars: Record<string, string | number> | undefined): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, name) => {
    const v = vars[name];
    return v === undefined ? `{${name}}` : String(v);
  });
}

/**
 * Translate a dotted key against the active catalogue. Falls back to the
 * English catalogue when the key is missing from the active one; if even
 * English is missing the key, returns the raw key (so a typo is obvious in
 * the UI rather than silently empty).
 */
export function t(key: string, vars?: Record<string, string | number>): string {
  const cat = CATALOGUES[activeLocale];
  const hit = lookup(cat, key) ?? lookup(en, key);
  return interpolate(hit ?? key, vars);
}

// ----------------------------------------------------------------------------
// Boot-time detection
// ----------------------------------------------------------------------------

/**
 * Pick a locale from a URL like `?lang=cs`. Returns `null` for "no override"
 * (the caller falls back to `navigator.language` and then `'en'`). Sanitises
 * to the registered set — anything unknown falls through.
 */
export function readLocaleFromUrl(
  search: string = typeof window !== 'undefined' ? window.location.search : '',
): LocaleId | null {
  try {
    const raw = new URLSearchParams(search).get('lang');
    if (raw === null) return null;
    const cleaned = raw.toLowerCase().trim();
    return isLocaleId(cleaned) ? cleaned : null;
  } catch {
    return null;
  }
}

/**
 * Map a `navigator.language` string (`'cs-CZ'`, `'en-GB'`, `'zh-CN'`,
 * `'zh-TW'`, `'pt-BR'`, `'de-AT'`) to a shipped locale id.
 *
 * The match is **prefix-based**: only the first sub-tag is consulted, so
 * regional variants resolve to their parent language when shipped. For
 * example `zh-CN` and `zh-TW` both resolve to `zh` (the shipped Chinese
 * catalogue is Simplified — there is no Traditional fallback in this pass,
 * a future locale can add one). Unknown leading sub-tags (`pt` if Portuguese
 * is not yet shipped) fall through to the next layer (then English).
 */
export function localeFromBrowser(lang: string | undefined): LocaleId | null {
  if (typeof lang !== 'string' || lang.length === 0) return null;
  const head = lang.toLowerCase().split(/[-_]/)[0]!;
  return isLocaleId(head) ? head : null;
}

/**
 * Resolve the initial locale at boot. Precedence: URL `?lang=…` override,
 * then `navigator.language`, then `'en'`. The result is sanitised — an
 * unknown value at any layer falls through to the next.
 */
export function detectInitialLocale(search?: string, browserLang?: string): LocaleId {
  const fromUrl = readLocaleFromUrl(search);
  if (fromUrl !== null) return fromUrl;
  const fromBrowser = localeFromBrowser(
    browserLang ?? (typeof navigator !== 'undefined' ? navigator.language : undefined),
  );
  if (fromBrowser !== null) return fromBrowser;
  return 'en';
}

/**
 * Apply boot-time detection in one call. Convenience wrapper used by
 * `Game.ts`. Returns the locale that was selected (useful for logging or
 * for tests that want to assert what happened).
 */
export function applyInitialLocale(): LocaleId {
  const id = detectInitialLocale();
  setLocale(id);
  return id;
}

/**
 * Advance to the next shipped locale (wrap-around). Used by the title-screen
 * LANG chip's click handler, which cycles through `LOCALES` in order so a
 * single-segment chip can pick from all 12 locales without a popup menu.
 */
export function cycleLocale(): LocaleId {
  const i = LOCALES.indexOf(activeLocale);
  const next = LOCALES[(i + 1) % LOCALES.length]!;
  setLocale(next);
  return next;
}

// ----------------------------------------------------------------------------
// Re-exports
// ----------------------------------------------------------------------------

export { en, type Catalogue } from './en';
export { cs } from './cs';
export { de } from './de';
export { pl } from './pl';
export { sk } from './sk';
export { hu } from './hu';
export { fr } from './fr';
export { es } from './es';
export { it } from './it';
export { zh } from './zh';
export { ja } from './ja';
export { ko } from './ko';

/** All catalogues keyed by id — useful for completeness tests. */
export const CATALOGUE_BY_ID: Readonly<Record<LocaleId, Catalogue>> = CATALOGUES;
